import {
  useSimulateContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useAccount,
} from 'wagmi';
import { encodeFunctionData } from 'viem';
import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import StakingABI from 'src/api/smart-contracts/abi/Staking';
import { CONTRACT_ADDRESSES } from 'src/constants';
import { trackSCWriteEvent } from 'src/utils';
import { useEstimateGas } from 'src/hooks/useEstimateGas';

export function useRedeem({
  requestIds,
  onSuccess,
}: {
  requestIds: string[];
  onSuccess?: (hash: string) => void;
}) {
  const { address } = useAccount();

  const idsBigInt = requestIds.map(BigInt);

  const encodedData =
    address && idsBigInt.length
      ? encodeFunctionData({
          abi: StakingABI,
          functionName: 'redeem',
          args: [idsBigInt, address],
        })
      : undefined;

  const {
    transactionCost,
    gasLimit,
    originalGasEstimate,
    isLoadingTransactionCost,
  } = useEstimateGas({
    encodedData,
    toAddress: CONTRACT_ADDRESSES.staking,
    claimCount: requestIds.length,
  });

  const { data: config, error: simulateError } = useSimulateContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'redeem',
    args: [idsBigInt, address!],
    gas: gasLimit,
    query: {
      enabled: !!address && !!requestIds.length,
    },
  });

  const {
    data: hash,
    isPending: isWriting,
    error: writeError,
    writeContract,
  } = useWriteContract();

  const confirmationEnabled = !!hash;

  const {
    isLoading: isConfirming,
    isSuccess,
    error: confirmError,
  } = useWaitForTransactionReceipt({
    hash,
    query: { enabled: confirmationEnabled },
  });

  useEffect(() => {
    if (isSuccess) {
      notifications.show({
        title: 'Claim completed',
        message: `Your ${requestIds.length === 1 ? '' : `${requestIds.length} `}claim${requestIds.length === 1 ? '' : 's'} have been completed`,
      });
      onSuccess?.(hash!);
      console.log('Claim completed with config:', config);
      trackSCWriteEvent('claim_completed', config, originalGasEstimate);
    }
  }, [isSuccess]);

  return {
    transactionCost: transactionCost.div(1e18),
    isLoadingTransactionCost,
    isRedeeming: isWriting || isConfirming,
    redeemError:
      simulateError ||
      writeError ||
      (confirmationEnabled ? confirmError : undefined),
    redeem: config?.request ? () => writeContract(config.request) : undefined,
  };
}
